import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: translate(-50%, -8px);
	}
	to {
		opacity: 1;
		transform: translate(-50%, 0);
	}
`;

const Positioner = styled.div`
	position: fixed;
	top: 84px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1000;
	pointer-events: none;

	@media (prefers-reduced-motion: no-preference) {
		animation: ${fadeIn} 0.2s ease-out;
	}
`;

const Pill = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 18px;
	border-radius: 999px;
	background-color: ${({ theme }) => theme.colors.modal};
	border: 1px solid ${({ theme }) => theme.colors.border};
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
	white-space: nowrap;
`;

const MessageText = styled.span`
	color: ${({ theme }) => theme.colors.textColor};
`;

const HintText = styled.span`
	color: ${({ theme }) => theme.colors.textAccent};
`;

/**
 * A non-blocking loading indicator anchored top-center over the map.
 * Rendered via a portal so it floats above the map regardless of mount point.
 *
 * NOTE: When migrating to antd, replace Pill with <Flex>, Spinner with <Spin>,
 * and MessageText/HintText with <Typography.Text>.
 */
const MapLoadingIndicator = ({ message = 'Loading', hint }) => {
	return createPortal(
		<Positioner role="status" aria-live="polite">
			<Pill>
				<Spinner animation="border" size="sm" />
				<MessageText>
					{message}
					{hint && <HintText> &middot; {hint}</HintText>}
				</MessageText>
			</Pill>
		</Positioner>,
		document.body
	);
};

export default MapLoadingIndicator;
